import React from 'react';

const ReminderNotification = ({ reminders = [], onDismiss = () => {}, onMarkComplete = () => {} }) => {
  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-red-500 bg-red-50';
      case 'medium': return 'border-yellow-500 bg-yellow-50';
      case 'low': return 'border-green-500 bg-green-50';
      default: return 'border-blue-500 bg-blue-50';
    }
  };

  const getTypeEmoji = (type) => {
    switch (type) {
      case 'call': return '📞';
      case 'email': return '📧';
      case 'meeting': return '🤝';
      case 'demo': return '💻';
      case 'proposal': return '📋';
      default: return '📅';
    }
  };

  // Don't render anything if no reminders
  if (!reminders || reminders.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-3 max-w-sm">
      {reminders.map((reminder) => (
        <div
          key={reminder.id}
          className={`bg-white border-l-4 rounded-lg shadow-2xl p-4 transform transition-all duration-500 ease-in-out animate-slide-in ${
            reminder.isOverdue ? 'border-red-500 bg-red-50' : getPriorityColor(reminder.priority)
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                reminder.isOverdue ? 'bg-red-100' : 'bg-orange-100'
              }`}>
                <span className="text-lg">
                  {reminder.isOverdue ? '⚠️' : getTypeEmoji(reminder.type)}
                </span>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <p className={`text-sm font-bold ${
                    reminder.isOverdue ? 'text-red-800' : 'text-orange-800'
                  }`}>
                    {reminder.isOverdue ? 'Follow-Up Overdue!' : 'Follow-Up Reminder'}
                  </p>
                  <button
                    onClick={() => onDismiss(reminder.id)}
                    className="text-gray-400 hover:text-gray-600 ml-2"
                  >
                    ✕
                  </button>
                </div>
                
                <p className="text-sm font-semibold text-gray-900 mt-1">
                  {reminder.leadName}
                </p>
                
                {reminder.leadCompany && (
                  <p className="text-xs text-gray-600">
                    {reminder.leadCompany}
                  </p>
                )}
                
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-gray-700">
                    <span className="font-medium">Type:</span> {reminder.type.charAt(0).toUpperCase() + reminder.type.slice(1)}
                  </p>
                  
                  <p className="text-xs text-gray-700">
                    <span className="font-medium">Scheduled:</span> {formatTime(reminder.scheduledDate)}
                  </p>
                  
                  {!reminder.isOverdue && (
                    <p className={`text-xs font-medium ${
                      reminder.timeLeft === '15 minutes' ? 'text-red-600' : 
                      reminder.timeLeft === '30 minutes' ? 'text-orange-600' : 'text-yellow-600'
                    }`}>
                      Due in {reminder.timeLeft}
                    </p>
                  )}
                  
                  {reminder.isOverdue && (
                    <p className="text-xs font-medium text-red-600">
                      Overdue!
                    </p>
                  )}
                  
                  {reminder.notes && (
                    <p className="text-xs text-gray-600 mt-1 italic">
                      "{reminder.notes}"
                    </p>
                  )}
                </div>
                
                <div className="flex space-x-2 mt-3">
                  <button
                    onClick={() => onMarkComplete(reminder.followUpId)}
                    className="text-xs bg-green-600 text-white px-3 py-1 rounded-full hover:bg-green-700 transition-colors"
                  >
                    Mark Complete
                  </button>
                  
                  <button
                    onClick={() => onDismiss(reminder.id)}
                    className="text-xs bg-gray-500 text-white px-3 py-1 rounded-full hover:bg-gray-600 transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Animated progress bar for urgency */}
          <div className="mt-3 bg-gray-200 rounded-full h-1">
            <div 
              className={`h-1 rounded-full transition-all duration-1000 ease-linear ${
                reminder.isOverdue ? 'bg-red-500' : 
                reminder.timeLeft === '15 minutes' ? 'bg-red-400' :
                reminder.timeLeft === '30 minutes' ? 'bg-orange-400' : 'bg-yellow-400'
              }`}
              style={{
                width: reminder.isOverdue ? '100%' : 
                       reminder.timeLeft === '15 minutes' ? '90%' :
                       reminder.timeLeft === '30 minutes' ? '70%' : '50%',
                animation: reminder.isOverdue ? 'pulse 2s infinite' : 'none'
              }}
            ></div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default ReminderNotification;
